import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocalParticipant, type TrackReferenceOrPlaceholder } from "@livekit/components-react";
import type { Track } from "livekit-client";
import { detectPipTier, type PipTier } from "../lib/pipSupport";
import { openDocumentPip, PIP_DEFAULT_SIZE } from "../lib/documentPip";
import { drawPipFrame, initialsFor, PIP_CANVAS_SIZE, type PipFrame } from "../lib/pipCanvas";
import { useSpeakerStage } from "./useSpeakerStage";

export interface CallPip {
  tier: PipTier;
  isOpen: boolean;
  /** The Document PiP window to portal controls into (document tier only). */
  pipWindow: Window | null;
  open: () => Promise<void>;
  close: () => void;
}

interface Options {
  /** e.g. "2 waiting"; null when nobody is in the waiting room. */
  waitingLabel: string | null;
  onToggleMic: () => void;
  onToggleCamera: () => void;
  onHangUp: () => void;
  /** Video tier: the user came back from the floating video to the page. */
  onReturnToCall?: () => void;
}

/** WebKit's presentation-mode API (Safari), absent from TS's DOM lib. */
type WebkitVideo = HTMLVideoElement & {
  autoPictureInPicture?: boolean;
  webkitSetPresentationMode?: (mode: "inline" | "picture-in-picture") => void;
  webkitPresentationMode?: string;
};
type CanvasWithStream = HTMLCanvasElement & {
  captureStream?: (fps?: number) => MediaStream;
};
type MediaSessionPip = MediaSession & {
  setMicrophoneActive?: (active: boolean) => void;
  setCameraActive?: (active: boolean) => void;
};

const MEDIA_ACTIONS = ["enterpictureinpicture", "togglemicrophone", "togglecamera", "hangup"];
const CANVAS_FPS = 15;
const HIDDEN_STYLE =
  "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:0;top:0";

/** The main tile's live video track, or null when muted / unsubscribed. */
function liveVideoTrack(ref: TrackReferenceOrPlaceholder | null): Track | null {
  const pub = ref?.publication;
  if (!pub || pub.isMuted || !pub.track?.mediaStreamTrack) return null;
  return pub.track;
}

/**
 * The canvas's source video is attached with the SDK, so Adaptive Stream
 * counts it as a viewer (#2223). It is PiP-sized and in the viewport -- an
 * off-screen or 1px element would read as "not shown" or "tiny" and the
 * floating video would freeze or blur once the call tiles are gone -- but
 * fully transparent and behind everything.
 */
const SOURCE_STYLE =
  `position:fixed;left:0;top:0;width:${PIP_CANVAS_SIZE.width}px;` +
  `height:${PIP_CANVAS_SIZE.height}px;opacity:0;pointer-events:none;z-index:-1`;

function hiddenVideo(style = HIDDEN_STYLE): HTMLVideoElement {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.autoplay = true;
  video.setAttribute("style", style);
  return video;
}

/** Start a hidden video explicitly; failures (no source yet) are harmless. */
function startPlayback(video: HTMLVideoElement): void {
  if (!video.srcObject || typeof video.play !== "function") return;
  try {
    void video.play()?.catch(() => {});
  } catch {
    // Not implemented (jsdom) or refused; autoplay remains the fallback.
  }
}

function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

function mediaSession(): MediaSessionPip | null {
  return typeof navigator !== "undefined" && navigator.mediaSession
    ? (navigator.mediaSession as MediaSessionPip)
    : null;
}

/**
 * The call's picture-in-picture controller. Mounted once per call (above the
 * in-call view ↔ floating widget swap) so changing suite modules never closes
 * the window. Document tier opens a styled always-on-top window the caller
 * portals controls into; video tier floats a hidden <video> showing the main
 * speaker (live track, or a canvas drawing of avatar + name when there is no
 * live video or someone is waiting to be admitted).
 *
 * Closing with the browser's ✕ counts as a dismissal: Media Session's
 * automatic `enterpictureinpicture` stops reopening it for the rest of the
 * call, while `open()` (the button) always works. `close()` is not a dismissal,
 * and neither is the browser closing an auto-opened window because the user
 * came back to the tab (it closes while the page is visible).
 *
 * Video tier (phones, Safari): the hidden video carries `autopictureinpicture`
 * so the browser may float it by itself when the user leaves, and a float the
 * browser started is tracked as open. Coming back to the page from the floating
 * video — its "return" control, or simply switching back — calls
 * `onReturnToCall`, and is never a dismissal; closing it while away is.
 */
export function useCallPictureInPicture(opts: Options): CallPip {
  const tier = useMemo(() => detectPipTier(), []);
  const { mainTrack } = useSpeakerStage({ excludeLocalFromMain: true });
  const { isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();

  const [isOpen, setIsOpen] = useState(false);
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const isOpenRef = useRef(false);
  const openingRef = useRef<Promise<void> | null>(null);
  const dismissedRef = useRef(false);
  /** Whether the open window came from Media Session's automatic request. */
  const autoOpenedRef = useRef(false);
  const windowRef = useRef<Window | null>(null);
  const videoRef = useRef<WebkitVideo | null>(null);
  const mountedRef = useRef(true);
  const optsRef = useRef(opts);
  useLayoutEffect(() => {
    optsRef.current = opts;
  });

  // Every close path funnels here. A ✕ from the browser is a dismissal; our own
  // close() runs this first, so the event the browser fires afterwards sees
  // `isOpenRef` already false (or a stale window) and is ignored.
  const markClosed = useCallback((dismissed: boolean) => {
    if (!isOpenRef.current) return;
    isOpenRef.current = false;
    windowRef.current = null;
    if (dismissed) dismissedRef.current = true;
    autoOpenedRef.current = false;
    if (!mountedRef.current) return;
    setIsOpen(false);
    setPipWindow(null);
  }, []);

  // A browser-side close of an auto-opened window while the page is visible
  // is the browser tidying up because the user returned to the tab, not a ✕.
  const markClosedByBrowser = useCallback(() => {
    const returnedToTab =
      autoOpenedRef.current && document.visibilityState === "visible";
    markClosed(!returnedToTab);
  }, [markClosed]);

  // Video tier: the browser floated the video itself (autopictureinpicture).
  const markOpenedByBrowser = useCallback(() => {
    if (isOpenRef.current) return;
    isOpenRef.current = true;
    autoOpenedRef.current = true;
    if (mountedRef.current) setIsOpen(true);
  }, []);

  // Video tier: the floating video went away. Seen from the page, the user
  // either came back (page visible: the return control, or switching back) or
  // closed it while elsewhere (page hidden), which is a dismissal.
  const markVideoLeft = useCallback(() => {
    if (!isOpenRef.current) return;
    const backOnPage = document.visibilityState === "visible";
    markClosed(!backOnPage);
    if (backOnPage) optsRef.current.onReturnToCall?.();
    if (!backOnPage && videoRef.current) {
      videoRef.current.autoPictureInPicture = false;
      videoRef.current.removeAttribute("autopictureinpicture");
    }
  }, [markClosed]);

  const openWindow = useCallback((auto: boolean): Promise<void> => {
    if (isOpenRef.current) return Promise.resolve();
    if (openingRef.current) return openingRef.current;
    // Everything up to the first await runs synchronously, inside the user's
    // click — browsers refuse PiP requests made outside a gesture.
    const pending = (async () => {
      try {
        if (tier === "document") {
          const win = await openDocumentPip(PIP_DEFAULT_SIZE);
          if (!mountedRef.current) {
            win.close();
            return;
          }
          windowRef.current = win;
          win.addEventListener(
            "pagehide",
            () => {
              if (windowRef.current === win) markClosedByBrowser();
            },
            { once: true },
          );
          isOpenRef.current = true;
          autoOpenedRef.current = auto;
          setPipWindow(win);
          setIsOpen(true);
        } else if (tier === "video") {
          const video = videoRef.current;
          if (!video) return;
          if (typeof video.requestPictureInPicture === "function") {
            await video.requestPictureInPicture();
          } else {
            video.webkitSetPresentationMode?.("picture-in-picture");
          }
          if (!mountedRef.current) return;
          isOpenRef.current = true;
          autoOpenedRef.current = auto;
          setIsOpen(true);
        }
      } catch (err) {
        console.warn("[call] picture-in-picture did not open", err);
      } finally {
        openingRef.current = null;
      }
    })();
    openingRef.current = pending;
    return pending;
  }, [tier, markClosedByBrowser]);

  const open = useCallback(() => openWindow(false), [openWindow]);

  const exitVideoPip = useCallback(() => {
    const video = videoRef.current;
    try {
      if (document.pictureInPictureElement === video && document.exitPictureInPicture) {
        void document.exitPictureInPicture().catch(() => {});
      } else if (video?.webkitPresentationMode === "picture-in-picture") {
        video.webkitSetPresentationMode?.("inline");
      }
    } catch {
      // Already closed by the browser.
    }
  }, []);

  const close = useCallback(() => {
    if (!isOpenRef.current) return;
    const win = windowRef.current;
    markClosed(false);
    if (win) win.close();
    else exitVideoPip();
  }, [markClosed, exitVideoPip]);

  // Video tier: the hidden <video> must exist before any click so open() can
  // request PiP synchronously. A second hidden video plays the live track so
  // the canvas can draw it under the waiting strip.
  const [media, setMedia] = useState<{
    source: HTMLVideoElement;
    canvas: CanvasWithStream;
    canvasStream: MediaStream | null;
  } | null>(null);
  useEffect(() => {
    if (tier !== "video") return;
    const video = hiddenVideo() as WebkitVideo;
    video.dataset.callPip = "";
    const source = hiddenVideo(SOURCE_STYLE);
    const canvas = document.createElement("canvas") as CanvasWithStream;
    canvas.width = PIP_CANVAS_SIZE.width;
    canvas.height = PIP_CANVAS_SIZE.height;
    // Let the browser float the call by itself when the user leaves (Safari
    // reads the attribute; others ignore it). Dismissing switches it off.
    video.autoPictureInPicture = true;
    video.setAttribute("autopictureinpicture", "");
    document.body.append(video, source);
    videoRef.current = video;
    const onEnter = () => markOpenedByBrowser();
    const onLeave = () => markVideoLeft();
    const onModeChange = () => {
      if (video.webkitPresentationMode === "picture-in-picture") markOpenedByBrowser();
      else markVideoLeft();
    };
    video.addEventListener("enterpictureinpicture", onEnter);
    video.addEventListener("leavepictureinpicture", onLeave);
    video.addEventListener("webkitpresentationmodechanged", onModeChange);
    setMedia({ source, canvas, canvasStream: canvas.captureStream?.(CANVAS_FPS) ?? null });
    return () => {
      video.removeEventListener("enterpictureinpicture", onEnter);
      video.removeEventListener("leavepictureinpicture", onLeave);
      video.removeEventListener("webkitpresentationmodechanged", onModeChange);
      // Removing the element alone may leave a frozen PiP window behind.
      if (document.pictureInPictureElement === video) {
        void document.exitPictureInPicture?.().catch(() => {});
      }
      video.remove();
      source.remove();
      videoRef.current = null;
    };
  }, [tier, markOpenedByBrowser, markVideoLeft]);

  // Video tier: switching back to the page while the video still floats (a
  // phone keeps it on screen) means the user wants the call again.
  useEffect(() => {
    if (tier !== "video") return;
    const onVisible = () => {
      if (document.visibilityState === "visible" && isOpenRef.current) {
        optsRef.current.onReturnToCall?.();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [tier]);

  const liveTrack = tier === "video" ? liveVideoTrack(mainTrack) : null;
  const liveMediaTrack = liveTrack?.mediaStreamTrack ?? null;
  const liveStream = useMemo(
    () =>
      liveMediaTrack && typeof MediaStream !== "undefined"
        ? new MediaStream([liveMediaTrack])
        : null,
    [liveMediaTrack],
  );

  // The canvas's source plays the live track through the SDK, so Adaptive
  // Stream keeps the main speaker's video flowing while it floats.
  useEffect(() => {
    if (!media || !liveTrack) return;
    liveTrack.attach(media.source);
    startPlayback(media.source);
    return () => {
      liveTrack.detach(media.source);
    };
  }, [media, liveTrack]);
  const showCanvas = !liveStream || opts.waitingLabel !== null;

  // Swap what the floating video shows; PiP is never re-requested for this.
  useEffect(() => {
    const video = videoRef.current;
    if (!media || !video) return;
    const next = showCanvas ? media.canvasStream : liveStream;
    // `autoplay` alone is not enough: WebKit may hold an invisible muted video
    // paused, and PiP then refuses to open (or floats a frozen frame).
    if (video.srcObject !== next) {
      video.srcObject = next;
      startPlayback(video);
    }
  }, [media, showCanvas, liveStream]);

  // What the canvas draws, read by the loop on every frame.
  const frameRef = useRef<Omit<PipFrame, "accent" | "accentInk">>({
    video: null,
    name: "",
    initials: "?",
    waitingLabel: null,
    micOn: true,
    camOn: true,
  });
  const participant = mainTrack?.participant;
  const name = participant ? participant.name || participant.identity : "";
  useEffect(() => {
    frameRef.current = {
      video: liveStream && media ? media.source : null,
      name,
      initials: initialsFor(name),
      waitingLabel: opts.waitingLabel,
      micOn: isMicrophoneEnabled,
      camOn: isCameraEnabled,
    };
  }, [liveStream, media, name, opts.waitingLabel, isMicrophoneEnabled, isCameraEnabled]);

  // Draw only while the canvas is what the video shows: at the capture rate
  // while PiP is open, about once a second while closed so a recent frame is
  // ready the moment PiP is requested. A timer rather than rAF, which stops in
  // background tabs (exactly when PiP matters); browsers clamp hidden-tab
  // timers to ~1 s, which is still enough for an avatar + name.
  useEffect(() => {
    if (!media || !showCanvas || !media.canvasStream) return;
    const ctx = media.canvas.getContext("2d");
    if (!ctx) return;
    // Theme colours change rarely: read them now and when <html> changes,
    // never per frame (getComputedStyle forces a style recalc).
    let accent = "";
    let accentInk = "";
    const readColors = () => {
      accent = cssVar("--accent", "#FFD71F");
      accentInk = cssVar("--color-primary-foreground", "#000");
    };
    readColors();
    const observer = new MutationObserver(readColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    const draw = () =>
      drawPipFrame(ctx, media.canvas.width, media.canvas.height, {
        ...frameRef.current,
        accent,
        accentInk,
      });
    draw();
    const timer = setInterval(draw, isOpen ? 1000 / CANVAS_FPS : 1000);
    return () => {
      clearInterval(timer);
      observer.disconnect();
    };
  }, [media, showCanvas, isOpen]);

  // Media Session actions. Each call is wrapped: browsers throw for actions
  // they don't know, and one unsupported action must not drop the others.
  const autoOpenRef = useRef(() => openWindow(true));
  useLayoutEffect(() => {
    autoOpenRef.current = () => openWindow(true);
  });
  useEffect(() => {
    const session = mediaSession();
    if (!session || tier === "none") return;
    const handlers: Record<string, () => void> = {
      enterpictureinpicture: () => {
        if (!dismissedRef.current) void autoOpenRef.current();
      },
      togglemicrophone: () => optsRef.current.onToggleMic(),
      togglecamera: () => optsRef.current.onToggleCamera(),
      hangup: () => optsRef.current.onHangUp(),
    };
    for (const action of MEDIA_ACTIONS) {
      try {
        session.setActionHandler(action as MediaSessionAction, handlers[action]);
      } catch {
        // Unsupported action in this browser.
      }
    }
    return () => {
      for (const action of MEDIA_ACTIONS) {
        try {
          session.setActionHandler(action as MediaSessionAction, null);
        } catch {
          // Unsupported action in this browser.
        }
      }
    };
  }, [tier]);

  useEffect(() => {
    const session = mediaSession();
    if (!session || tier === "none") return;
    try {
      session.setMicrophoneActive?.(isMicrophoneEnabled);
    } catch {
      // Not supported.
    }
    try {
      session.setCameraActive?.(isCameraEnabled);
    } catch {
      // Not supported.
    }
  }, [tier, isMicrophoneEnabled, isCameraEnabled]);

  // The call ended: the host unmounts this hook with the session.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Video-tier PiP is exited by the hidden video's own cleanup above.
      windowRef.current?.close();
      windowRef.current = null;
      isOpenRef.current = false;
    };
  }, []);

  return { tier, isOpen, pipWindow, open, close };
}
